import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Button,
  Form,
  Spinner,
  Table,
  Modal,
  Tab,
  Tabs,
} from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import NavContainer from '../../components/Nav';
import OffCanvasNav from '../../components/OffCanvasNav';
import AlertModal from '../../components/AlertModal';
import useAlertModal from '../../hooks/useAlertModal';
import {
  getSizeSpecs,
  createSizeSpec,
  updateSizeSpec,
  deleteSizeSpec,
  getGarmentTypes,
  createGarmentType,
  updateGarmentType,
  deleteGarmentType,
} from '../../services/sizeSpecGarmentTypeApi';
import { sortBySizeCode } from '../../utils/sizeOrder';
import { generateMeasuresPdf } from '../../utils/measuresPdfGenerator';
import { useBranding } from '../../context/BrandingContext';
import './SizeSpecGarmentTypeManagement.css';

const extractArray = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.message)) return data.message;
  if (Array.isArray(data.data)) return data.data;
  return [];
};

/** Columnas de medidas según el backend (sizeSpec) */
const MEASURE_COLUMNS = [
  { key: 'chest_girth', label: 'Contorno pecho (cm)' },
  { key: 'back_length', label: 'Largo espalda (cm)' },
  { key: 'neck_girth', label: 'Contorno cuello (cm)' },
  { key: 'approx_weight', label: 'Peso aprox. (kg)' },
  { key: 'breed_type', label: 'Tipo de raza' },
];

const getMeasureValue = (item, key) => {
  if (!item) return null;
  let v = item[key];
  if (v != null && v !== '') return v;
  for (const nest of ['measurements', 'medidas', 'measures']) {
    const obj = item[nest];
    if (obj && typeof obj === 'object') {
      v = obj[key];
      if (v != null && v !== '') return v;
    }
  }
  return null;
};

const measureColumns = MEASURE_COLUMNS.map((c) => c.key);

const SizeSpecGarmentTypeManagement = () => {
  const { showAlert, showAlertModal, alertMessage, alertType, hideAlert, getErrorMessage } = useAlertModal();
  const branding = useBranding();

  const [activeTab, setActiveTab] = useState('medidas');
  const [sizeSpecs, setSizeSpecs] = useState([]);
  const [garmentTypes, setGarmentTypes] = useState([]);
  const [loadingSizes, setLoadingSizes] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  const [showSizeModal, setShowSizeModal] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [showSizeDeleteModal, setShowSizeDeleteModal] = useState(false);
  const [showTypeDeleteModal, setShowTypeDeleteModal] = useState(false);
  const [editingSize, setEditingSize] = useState(null);
  const [editingType, setEditingType] = useState(null);

  const [sizeForm, setSizeForm] = useState({ size_code: '', size_variant: '' });
  const [typeForm, setTypeForm] = useState({
    name: '',
    complexity_factor: '',
    default_labor_cost: '',
    default_supplies_cost: '',
  });

  const loadSizeSpecs = useCallback(async () => {
    setLoadingSizes(true);
    const { ok, data } = await getSizeSpecs();
    setLoadingSizes(false);
    if (ok) {
      setSizeSpecs(sortBySizeCode(extractArray(data)));
    } else {
      showAlert(getErrorMessage({ data }) || 'Error cargando tabla de medidas', 'error');
    }
  }, [showAlert, getErrorMessage]);

  const loadGarmentTypes = useCallback(async () => {
    setLoadingTypes(true);
    const { ok, data } = await getGarmentTypes();
    setLoadingTypes(false);
    if (ok) {
      setGarmentTypes(extractArray(data));
    } else {
      showAlert(getErrorMessage({ data }) || 'Error cargando tipos de prenda', 'error');
    }
  }, [showAlert, getErrorMessage]);

  useEffect(() => {
    loadSizeSpecs();
  }, [loadSizeSpecs]);

  useEffect(() => {
    loadGarmentTypes();
  }, [loadGarmentTypes]);

  const openCreateSize = () => {
    setEditingSize(null);
    setSizeForm({ size_code: '', size_variant: '', ...Object.fromEntries(measureColumns.map((k) => [k, ''])) });
    setShowSizeModal(true);
  };

  const openEditSize = (item) => {
    setEditingSize(item);
    setSizeForm({
      size_code: item.size_code || '',
      size_variant: item.size_variant || item.variant || '',
      ...Object.fromEntries(measureColumns.map((k) => [k, getMeasureValue(item, k) ?? ''])),
    });
    setShowSizeModal(true);
  };

  const openDeleteSize = (item) => {
    setEditingSize(item);
    setShowSizeDeleteModal(true);
  };

  const openCreateType = () => {
    setEditingType(null);
    setTypeForm({
      name: '',
      complexity_factor: '',
      default_labor_cost: '',
      default_supplies_cost: '',
    });
    setShowTypeModal(true);
  };

  const openEditType = (item) => {
    setEditingType(item);
    setTypeForm({
      name: item.name || item.garment_type_name || '',
      complexity_factor: item.complexity_factor ?? '',
      default_labor_cost: item.default_labor_cost ?? item.labor_cost ?? '',
      default_supplies_cost: item.default_supplies_cost ?? item.supplies_cost ?? '',
    });
    setShowTypeModal(true);
  };

  const openDeleteType = (item) => {
    setEditingType(item);
    setShowTypeDeleteModal(true);
  };

  const handleSizeSubmit = async (e) => {
    e.preventDefault();
    if (!sizeForm.size_code?.trim()) {
      showAlert('El código de talla es obligatorio', 'error');
      return;
    }
    setModalLoading(true);
    try {
      const payload = {
        size_code: sizeForm.size_code.trim(),
        size_variant: sizeForm.size_variant?.trim() || undefined,
      };
      for (const c of MEASURE_COLUMNS) {
        const v = sizeForm[c.key];
        if (c.key === 'breed_type') {
          if (v?.trim()) payload.breed_type = v.trim();
        } else if (v !== '' && v != null) {
          const num = parseFloat(v);
          if (!isNaN(num)) payload[c.key] = num;
        }
      }
      const { ok, status, data } = editingSize
        ? await updateSizeSpec(editingSize.id, payload)
        : await createSizeSpec(payload);
      if (ok && (status === 200 || status === 201 || data?.code === 200 || data?.code === 201)) {
        showAlert(data?.message || (editingSize ? 'Medida actualizada' : 'Medida creada'), 'success');
        setShowSizeModal(false);
        loadSizeSpecs();
      } else {
        showAlert(getErrorMessage({ data }) || 'Error al guardar', 'error');
      }
    } catch (err) {
      showAlert('Error inesperado', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const handleTypeSubmit = async (e) => {
    e.preventDefault();
    if (!typeForm.name?.trim()) {
      showAlert('El nombre es obligatorio', 'error');
      return;
    }
    setModalLoading(true);
    try {
      const payload = {
        name: typeForm.name.trim(),
        complexity_factor: typeForm.complexity_factor ? parseFloat(typeForm.complexity_factor) : undefined,
        default_labor_cost: typeForm.default_labor_cost ? parseFloat(typeForm.default_labor_cost) : undefined,
        default_supplies_cost: typeForm.default_supplies_cost ? parseFloat(typeForm.default_supplies_cost) : undefined,
      };
      const { ok, status, data } = editingType
        ? await updateGarmentType(editingType.id ?? editingType.id_garment_type, payload)
        : await createGarmentType(payload);
      if (ok && (status === 200 || status === 201 || data?.code === 200 || data?.code === 201)) {
        showAlert(data?.message || (editingType ? 'Tipo actualizado' : 'Tipo creado'), 'success');
        setShowTypeModal(false);
        loadGarmentTypes();
      } else {
        showAlert(getErrorMessage({ data }) || 'Error al guardar', 'error');
      }
    } catch (err) {
      showAlert('Error inesperado', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const handleConfirmDeleteSize = async () => {
    if (!editingSize?.id) return;
    setModalLoading(true);
    try {
      const { ok, status, data } = await deleteSizeSpec(editingSize.id);
      if (ok && status === 200) {
        showAlert(data?.message || 'Medida eliminada', 'success');
        setShowSizeDeleteModal(false);
        setEditingSize(null);
        loadSizeSpecs();
      } else {
        showAlert(getErrorMessage({ data }) || 'Error al eliminar', 'error');
      }
    } catch (err) {
      showAlert('Error inesperado', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const handleConfirmDeleteType = async () => {
    const id = editingType?.id ?? editingType?.id_garment_type;
    if (!id) return;
    setModalLoading(true);
    try {
      const { ok, status, data } = await deleteGarmentType(id);
      if (ok && status === 200) {
        showAlert(data?.message || 'Tipo eliminado', 'success');
        setShowTypeDeleteModal(false);
        setEditingType(null);
        loadGarmentTypes();
      } else {
        showAlert(getErrorMessage({ data }) || 'Error al eliminar', 'error');
      }
    } catch (err) {
      showAlert('Error inesperado', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const sizeLabel = (item) =>
    [item.size_code, item.size_variant || item.variant].filter(Boolean).join(' - ') || `ID ${item.id}`;
  const typeLabel = (item) => item.name || item.garment_type_name || `Tipo ${item.id}`;

  return (
    <>
      <Helmet>
        <title>Administración - Medidas y Tipos de Prenda - DotoniWoof</title>
      </Helmet>
      <NavContainer />
      <OffCanvasNav />

      <div className="admin-measures-container">
        <div className="admin-measures-header">
          <h1 className="admin-measures-title">Administración de Medidas y Tipos de Prenda</h1>
          <p className="admin-measures-subtitle">
            Gestiona la tabla de medidas (tallas) y los tipos de prenda para la calculadora de costos.
          </p>
        </div>

        <Card className="admin-measures-card">
          <Card.Body>
            <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'medidas')} className="admin-measures-tabs">
              <Tab eventKey="medidas" title="Tabla de medidas">
                <div className="admin-measures-tab-content">
                  <div className="admin-measures-toolbar">
                    <Button
                      variant="outline-secondary"
                      className="admin-measures-btn-pdf"
                      onClick={() =>
                        generateMeasuresPdf(sizeSpecs, getMeasureValue, MEASURE_COLUMNS, { companyName: branding.companyName, logoUrl: branding.logoUrl }).catch(() => {
                          showAlert('Error al generar el PDF', 'error');
                        })
                      }
                      disabled={loadingSizes}
                    >
                      📄 Descargar PDF
                    </Button>
                    <Button className="admin-measures-btn-add" onClick={openCreateSize}>
                      + Nueva medida
                    </Button>
                  </div>
                  {loadingSizes ? (
                    <div className="admin-measures-loading">
                      <Spinner animation="border" />
                      <span>Cargando medidas...</span>
                    </div>
                  ) : (
                    <div className="admin-measures-table-wrap">
                      <Table responsive className="admin-measures-table">
                        <thead>
                          <tr>
                            <th>Código talla</th>
                            <th>Variante</th>
                            {MEASURE_COLUMNS.map((c) => (
                              <th key={c.key}>{c.label}</th>
                            ))}
                            <th className="text-end">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sizeSpecs.length === 0 ? (
                            <tr>
                              <td colSpan={2 + MEASURE_COLUMNS.length + 1} className="text-center text-muted py-4">
                                No hay medidas registradas
                              </td>
                            </tr>
                          ) : (
                            sizeSpecs.map((item) => (
                              <tr key={item.id}>
                                <td>{item.size_code || '-'}</td>
                                <td>{item.size_variant || item.variant || '-'}</td>
                                {MEASURE_COLUMNS.map((c) => {
                                  const val = getMeasureValue(item, c.key);
                                  const display = val != null && val !== '' ? String(val) : '-';
                                  return (
                                    <td key={c.key}>{display}</td>
                                  );
                                })}
                                <td className="text-end">
                                  <Button variant="outline-primary" size="sm" className="me-1" onClick={() => openEditSize(item)}>
                                    Editar
                                  </Button>
                                  <Button variant="outline-danger" size="sm" onClick={() => openDeleteSize(item)}>
                                    Eliminar
                                  </Button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              </Tab>

              <Tab eventKey="tipos" title="Tipos de prenda">
                <div className="admin-measures-tab-content">
                  <div className="admin-measures-info-box">
                    <h6 className="admin-measures-info-title">
                      <span className="admin-measures-info-icon">ℹ️</span>
                      ¿Qué es el factor de complejidad y cómo calcularlo?
                    </h6>
                    <p className="admin-measures-info-text">
                      El <strong>factor de complejidad</strong> es un multiplicador que ajusta el costo de confección según la dificultad de elaborar cada tipo de prenda. Una prenda más compleja (más piezas, acabados especiales, detalles) tendrá un factor mayor.
                    </p>
                    <p className="admin-measures-info-text mb-0">
                      <strong>Cómo calcularlo:</strong> Usa como referencia una prenda básica (ej: camiseta simple) con factor <strong>1.0</strong>. Si otra prenda requiere el doble de tiempo o esfuerzo, usa factor <strong>2.0</strong>. Si es más sencilla (mitad del tiempo), usa <strong>0.5</strong>. Valores típicos: básico 1.0, intermedio 1.2–1.5, complejo 1.8–2.5.
                    </p>
                  </div>
                  <div className="admin-measures-toolbar">
                    <Button className="admin-measures-btn-add" onClick={openCreateType}>
                      + Nuevo tipo de prenda
                    </Button>
                  </div>
                  {loadingTypes ? (
                    <div className="admin-measures-loading">
                      <Spinner animation="border" />
                      <span>Cargando tipos...</span>
                    </div>
                  ) : (
                    <Table responsive className="admin-measures-table">
                      <thead>
                        <tr>
                          <th>Nombre</th>
                          <th>Factor complejidad</th>
                          <th>Mano obra default</th>
                          <th>Insumos default</th>
                          <th className="text-end">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {garmentTypes.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="text-center text-muted py-4">
                              No hay tipos de prenda registrados
                            </td>
                          </tr>
                        ) : (
                          garmentTypes.map((item) => (
                            <tr key={item.id ?? item.id_garment_type}>
                              <td>{item.name || item.garment_type_name || '-'}</td>
                              <td>{item.complexity_factor ?? '-'}</td>
                              <td>{item.default_labor_cost ?? item.labor_cost != null ? `$ ${Number(item.default_labor_cost ?? item.labor_cost).toLocaleString('es-CO')}` : '-'}</td>
                              <td>{item.default_supplies_cost ?? item.supplies_cost != null ? `$ ${Number(item.default_supplies_cost ?? item.supplies_cost).toLocaleString('es-CO')}` : '-'}</td>
                              <td className="text-end">
                                <Button variant="outline-primary" size="sm" className="me-1" onClick={() => openEditType(item)}>
                                  Editar
                                </Button>
                                <Button variant="outline-danger" size="sm" onClick={() => openDeleteType(item)}>
                                  Eliminar
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  )}
                </div>
              </Tab>
            </Tabs>
          </Card.Body>
        </Card>
      </div>

      <Modal show={showSizeModal} onHide={() => setShowSizeModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingSize ? 'Editar medida' : 'Nueva medida'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSizeSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Código de talla *</Form.Label>
              <Form.Control
                name="size_code"
                value={sizeForm.size_code}
                onChange={(e) => setSizeForm((p) => ({ ...p, size_code: e.target.value }))}
                placeholder="Ej: S, M, L"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Variante / Tamaño</Form.Label>
              <Form.Control
                name="size_variant"
                value={sizeForm.size_variant}
                onChange={(e) => setSizeForm((p) => ({ ...p, size_variant: e.target.value }))}
                placeholder="Ej: PEQUEÑO, GRANDE"
              />
            </Form.Group>
            <hr className="my-3" />
            <h6 className="mb-3">Medidas</h6>
            <div className="row g-2">
              {MEASURE_COLUMNS.map((c) => (
                <div key={c.key} className="col-6 col-md-4">
                  <Form.Group className="mb-2">
                    <Form.Label className="small">{c.label}</Form.Label>
                    <Form.Control
                      type={c.key === 'breed_type' ? 'text' : 'number'}
                      step={c.key === 'approx_weight' ? '0.1' : '0.01'}
                      min={c.key === 'breed_type' ? undefined : '0'}
                      name={c.key}
                      value={sizeForm[c.key] ?? ''}
                      onChange={(e) => setSizeForm((p) => ({ ...p, [c.key]: e.target.value }))}
                      placeholder={c.key === 'breed_type' ? 'Ej: Grande' : '0'}
                    />
                  </Form.Group>
                </div>
              ))}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowSizeModal(false)}>Cancelar</Button>
            <Button variant="primary" type="submit" disabled={modalLoading}>
              {modalLoading ? <Spinner animation="border" size="sm" /> : (editingSize ? 'Guardar' : 'Crear')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showTypeModal} onHide={() => setShowTypeModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingType ? 'Editar tipo de prenda' : 'Nuevo tipo de prenda'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleTypeSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Nombre *</Form.Label>
              <Form.Control
                name="name"
                value={typeForm.name}
                onChange={(e) => setTypeForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="Ej: Camiseta, Pantalón"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Factor de complejidad</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                name="complexity_factor"
                value={typeForm.complexity_factor}
                onChange={(e) => setTypeForm((p) => ({ ...p, complexity_factor: e.target.value }))}
                placeholder="Opcional"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Mano de obra por defecto</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                name="default_labor_cost"
                value={typeForm.default_labor_cost}
                onChange={(e) => setTypeForm((p) => ({ ...p, default_labor_cost: e.target.value }))}
                placeholder="Opcional"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Insumos por defecto</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                name="default_supplies_cost"
                value={typeForm.default_supplies_cost}
                onChange={(e) => setTypeForm((p) => ({ ...p, default_supplies_cost: e.target.value }))}
                placeholder="Opcional"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowTypeModal(false)}>Cancelar</Button>
            <Button variant="primary" type="submit" disabled={modalLoading}>
              {modalLoading ? <Spinner animation="border" size="sm" /> : (editingType ? 'Guardar' : 'Crear')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showSizeDeleteModal} onHide={() => setShowSizeDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Eliminar medida</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Eliminar la medida <strong>{editingSize && sizeLabel(editingSize)}</strong>? Esta acción no se puede deshacer.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSizeDeleteModal(false)}>Cancelar</Button>
          <Button variant="danger" onClick={handleConfirmDeleteSize} disabled={modalLoading}>
            {modalLoading ? <Spinner animation="border" size="sm" /> : 'Eliminar'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showTypeDeleteModal} onHide={() => setShowTypeDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Eliminar tipo de prenda</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Eliminar el tipo de prenda <strong>{editingType && typeLabel(editingType)}</strong>? Esta acción no se puede deshacer.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTypeDeleteModal(false)}>Cancelar</Button>
          <Button variant="danger" onClick={handleConfirmDeleteType} disabled={modalLoading}>
            {modalLoading ? <Spinner animation="border" size="sm" /> : 'Eliminar'}
          </Button>
        </Modal.Footer>
      </Modal>

      <AlertModal show={showAlertModal} onHide={hideAlert} message={alertMessage} type={alertType} />
    </>
  );
};

export default SizeSpecGarmentTypeManagement;
